/**
 * A QR code for the join URL.
 *
 * Rendered as an inline SVG of the module grid, so it stays sharp on a
 * television at any size and needs no canvas or image round trip.
 *
 * The encoder is a dependency rather than something written here: Reed-Solomon
 * error correction is a solved problem that is easy to get subtly wrong, and a
 * QR code that *looks* right and does not scan is worse than no QR code at all.
 * `qrcode-generator` is MIT, has no dependencies of its own, and this package
 * is not under the runtime-dependency gate - that covers engine, toolkit,
 * nakama-server and protocol.
 */

import qrcode from 'qrcode-generator';

export function QrCode({ text, size = 132 }: { text: string; size?: number }) {
  let modules: boolean[][];
  let count: number;

  try {
    // Type 0 picks the smallest version that fits; 'M' tolerates ~15% damage,
    // which is the usual choice for a screen somebody photographs at an angle.
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();

    count = qr.getModuleCount();
    modules = [];
    for (let row = 0; row < count; row++) {
      const cells: boolean[] = [];
      for (let column = 0; column < count; column++) cells.push(qr.isDark(row, column));
      modules.push(cells);
    }
  } catch {
    // A URL too long to encode should cost the QR code, not the screen.
    return null;
  }

  // A quiet zone of four modules is part of the spec, not decoration: scanners
  // need the margin to find the symbol.
  const quiet = 4;
  const span = count + quiet * 2;

  return (
    <svg
      className="qr"
      width={size}
      height={size}
      viewBox={`0 0 ${String(span)} ${String(span)}`}
      role="img"
      aria-label="Scan to join this room"
      shapeRendering="crispEdges"
    >
      <rect width={span} height={span} fill="#ffffff" />
      {modules.map((cells, row) =>
        cells.map((dark, column) =>
          dark ? (
            <rect
              key={`${String(row)}-${String(column)}`}
              x={column + quiet}
              y={row + quiet}
              width={1}
              height={1}
              fill="#0f1720"
            />
          ) : null,
        ),
      )}
    </svg>
  );
}
